import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Alert,
  Stack 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
}));

const ErrorCard = styled(Card)(({ theme }) => ({
  maxWidth: 600,
  width: '100%',
  textAlign: 'center',
}));

const ErrorIcon = styled(BugReportIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.error.main,
  marginBottom: theme.spacing(2),
}));

function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const isDevelopment = process.env.NODE_ENV === 'development';

  const handleReload = () => {
    window.location.reload();
  };

  const handleReportError = () => {
    // In a real app, you would send this to your error reporting service
    console.error('Error reported:', error);
    
    // You could integrate with services like Sentry, LogRocket, etc.
    // Example: Sentry.captureException(error);
  };

  return (
    <ErrorContainer>
      <ErrorCard>
        <CardContent>
          <ErrorIcon />
          
          <Typography variant="h4" gutterBottom color="error">
            Oops! Something went wrong
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            We're sorry, but something unexpected happened. Our team has been notified 
            and is working to fix the issue.
          </Typography>

          {isDevelopment && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="subtitle2" gutterBottom>
                Development Error Details:
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                whiteSpace: 'pre-wrap',
                fontSize: '0.75rem',
                fontFamily: 'monospace'
              }}>
                {error.message}
              </Typography>
              {error.stack && (
                <Typography variant="body2" component="pre" sx={{ 
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.7rem',
                  fontFamily: 'monospace',
                  mt: 1,
                  opacity: 0.8
                }}>
                  {error.stack}
                </Typography>
              )}
            </Alert>
          )}

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={resetErrorBoundary}
              size="large"
            >
              Try Again
            </Button>
            
            <Button
              variant="outlined"
              onClick={handleReload}
              size="large"
            >
              Reload Page
            </Button>
            
            <Button
              variant="text"
              onClick={handleReportError}
              size="large"
            >
              Report Issue
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
            If this problem persists, please contact our support team.
          </Typography>
        </CardContent>
      </ErrorCard>
    </ErrorContainer>
  );
}

export default ErrorFallback;